import type { ReactNode } from 'react';
import { Label } from 'src/components/ui/label';
import { MultiSelect } from 'src/components/common/MultiSelect';
import { PagePlacementField } from 'src/components/task-pages/PagePlacementField';
import type { PagePlacement } from 'src/components/task-pages/pagePlacement';
import { usePageEditorOptions } from 'src/components/task-pages/usePageEditorOptions';
import { SortOrderField } from './SortOrderField';

type PlacementAudienceDraft = PagePlacement & {
  sortOrder: string;
  audienceUserGroupIds: string[];
};

/**
 * Sidebar section, sort order and audience: the fields the task page and
 * agent page editors share. The caller supplies the audience wording, which
 * differs per page kind.
 */
export function PagePlacementAudienceFields<T extends PlacementAudienceDraft>({
  idPrefix,
  draft,
  onChange,
  audienceLabel,
  audiencePlaceholder,
  audienceHint,
}: {
  idPrefix: string;
  draft: T;
  onChange: (next: T) => void;
  audienceLabel: ReactNode;
  audiencePlaceholder: string;
  audienceHint: ReactNode;
}) {
  const { placementOptions, groupOptions, selectedAudience } =
    usePageEditorOptions(draft.audienceUserGroupIds);
  return (
    <>
      <PagePlacementField
        id={`${idPrefix}-nav-group`}
        placement={draft}
        options={placementOptions}
        onChange={(placement) => onChange({ ...draft, ...placement })}
      />
      <SortOrderField
        id={`${idPrefix}-sort-order`}
        value={draft.sortOrder}
        onChange={(sortOrder) => onChange({ ...draft, sortOrder })}
      />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-audience`}>{audienceLabel}</Label>
        <MultiSelect
          options={groupOptions}
          value={selectedAudience}
          onChange={(selected) =>
            onChange({
              ...draft,
              audienceUserGroupIds: selected.map((o) => o.id),
            })
          }
          placeholder={audiencePlaceholder}
          fullWidth
        />
        <p className="text-[11px] text-muted-foreground">{audienceHint}</p>
      </div>
    </>
  );
}
